import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Mail, User, MessageSquare, CheckCircle2 } from 'lucide-react';
import AppNav from '@/components/AppNav';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const SupportPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/support/contact`, { name, email, message });
      setSent(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch {
      toast.error('Failed to send message. Please email hey@poettik.com directly.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO title="Contact Support" description="Get in touch with the Poettik team for account help, billing questions or product feedback." />
      <nav className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16">
          <AppNav />
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-lg" data-testid="support-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Contact Support</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">We're here to help</p>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Message Sent</h3>
                <p className="text-sm text-muted-foreground mb-6">We'll get back to you as soon as possible.</p>
                <Link to="/"><Button variant="outline">Back to Home</Button></Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input className="pl-10" value={name} onChange={e => setName(e.target.value)} required placeholder="Your name" data-testid="support-name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="email" className="pl-10" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="support-email" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>How can we help?</Label>
                  <Textarea className="min-h-[120px]" value={message} onChange={e => setMessage(e.target.value)} required placeholder="Describe your issue or question..." data-testid="support-message" />
                </div>
                <Button type="submit" variant="premium" className="w-full" disabled={loading} data-testid="support-submit">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                  Send Message
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupportPage;
